import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import {
  addOptimisticManualRows,
  reconcileCreatedManualRowCells,
} from "@/payroll/ai-script/spreadsheet-cache";
import { assignCreatedManualRow } from "@/payroll/ai-script/spreadsheet-cache/pending-manual-rows";
import { aiScriptSpreadsheetSheetKey } from "@/payroll/ai-script/spreadsheet-sheet-query";
import type {
  CachedSpreadsheetSheetResponse,
  IManualRowCellInput,
  IManualRowInput,
  IManualRowsResponse,
} from "@/payroll/ai-script/types";
import {
  isRecoverableWorkbookOperationError,
  workbookMutationRequestConfig,
} from "@/payroll/ai-script/workbook-mutation";
import { aiScriptManualRowsKey } from "./keys";
import { getAiScriptManualRowsPath } from "./paths";
import {
  executeWorkbookMutation,
  invalidateOptimisticSheetOnMutationError,
} from "./workbook-refresh";

interface CreateManualRowParams {
  runId: string;
  sheetId: string;
  cells?: Record<string, IManualRowCellInput>;
  rows?: IManualRowInput[];
  anchor?: { after_row?: number };
  baseVersion?: number;
  optimisticToken?: string;
}

export const canBufferOptimisticManualRowEdits = (
  optimisticToken: string | undefined,
  rowInputCount: number,
): boolean => optimisticToken != null && rowInputCount === 1;

export const useGetAiScriptManualRows = (
  businessId: string | null,
  runId: string | null,
  enabled = true,
) => {
  const api = useApi();

  return useQuery({
    queryKey: aiScriptManualRowsKey(businessId, runId),
    queryFn: () => {
      if (!businessId || !runId) throw new Error("Payroll run is required");

      return api
        .get(getAiScriptManualRowsPath(businessId, runId))
        .then((res) => res.data as IManualRowsResponse);
    },
    enabled: enabled && !!businessId && !!runId,
    retry: false,
  });
};

export const useCreateManualRow = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      runId,
      sheetId,
      cells,
      rows,
      anchor,
      baseVersion,
      optimisticToken,
    }: CreateManualRowParams) => {
      if (!businessId) throw new Error("Business is required");

      const versionField =
        baseVersion != null ? { base_version: baseVersion } : {};
      const body = rows
        ? { sheet_id: sheetId, rows, ...versionField }
        : { sheet_id: sheetId, cells, anchor, ...versionField };

      return executeWorkbookMutation({
        api,
        queryClient,
        businessId,
        runId,
        onApplied: (result) => {
          const manualRowId = result.manual_row_ids?.[0];
          if (!optimisticToken) return;
          queryClient.setQueryData<CachedSpreadsheetSheetResponse>(
            aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
            (data) =>
              manualRowId
                ? assignCreatedManualRow(data, optimisticToken, manualRowId)
                : reconcileCreatedManualRowCells(data, [], optimisticToken),
          );
        },
        submit: (idempotencyKey) =>
          api
            .post(
              getAiScriptManualRowsPath(businessId, runId),
              body,
              workbookMutationRequestConfig(idempotencyKey),
            )
            .then((res) => res.data),
      });
    },
    onMutate: async ({
      runId,
      sheetId,
      cells,
      rows,
      anchor,
      optimisticToken,
    }) => {
      const sheetKey = aiScriptSpreadsheetSheetKey(businessId, runId, sheetId);
      await queryClient.cancelQueries({ queryKey: sheetKey });
      if (!queryClient.getQueryData<CachedSpreadsheetSheetResponse>(sheetKey)) {
        return {};
      }

      const rowInputs = rows ?? (cells ? [{ cells }] : []);
      const afterRow = anchor?.after_row ?? null;
      const token = optimisticToken ?? crypto.randomUUID();

      queryClient.setQueryData<CachedSpreadsheetSheetResponse>(
        sheetKey,
        (data) =>
          addOptimisticManualRows(data, {
            sheetId,
            rowInputs,
            token,
            afterRow,
            editableWhilePending: canBufferOptimisticManualRowEdits(
              optimisticToken,
              rowInputs.length,
            ),
          }),
      );

      return { sheetKey, optimisticToken: token };
    },
    onError: (error, _variables, context) => {
      if (!isRecoverableWorkbookOperationError(error) && context?.sheetKey) {
        queryClient.setQueryData<CachedSpreadsheetSheetResponse>(
          context.sheetKey,
          (data) =>
            reconcileCreatedManualRowCells(data, [], context.optimisticToken),
        );
      }
      invalidateOptimisticSheetOnMutationError(queryClient, error, context);
    },
    onSuccess: (response, { runId, sheetId, optimisticToken }, context) => {
      if (optimisticToken && response.manual_row_ids?.length) return;
      queryClient.setQueryData<CachedSpreadsheetSheetResponse>(
        aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
        (data) =>
          reconcileCreatedManualRowCells(data, [], context?.optimisticToken),
      );
    },
  });
};
